//# sourceMappingURL=popover.mjs.map
/// <reference types="./popover.d.mts" />
import * as $bool from "../../../gleam_stdlib/gleam/bool.mjs";
import * as $option from "../../../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../../../gleam_stdlib/gleam/option.mjs";
import * as $redraw from "../../../redraw/redraw.mjs";
import * as $context from "../../../redraw_unsafe/redraw/unsafe/context.mjs";
import * as $unsafe from "../../ds/internals/unsafe.mjs";
import * as $scroll from "../../ds/providers/scroll.mjs";
import { Ok, CustomType as $CustomType, makeError, isEqual } from "../../gleam.mjs";

const FILEPATH = "src/ds/providers/popover.gleam";

export class Popover extends $CustomType {
  constructor(current_opened, open, close) {
    super();
    this.current_opened = current_opened;
    this.open = open;
    this.close = close;
  }
}
export const Popover$Popover = (current_opened, open, close) =>
  new Popover(current_opened, open, close);
export const Popover$isPopover = (value) => value instanceof Popover;
export const Popover$Popover$current_opened = (value) => value.current_opened;
export const Popover$Popover$0 = (value) => value.current_opened;
export const Popover$Popover$open = (value) => value.open;
export const Popover$Popover$1 = (value) => value.open;
export const Popover$Popover$close = (value) => value.close;
export const Popover$Popover$2 = (value) => value.close;

const context_name = "Freyja:Providers:Popover";

function use_document_click(current_opened, set_current_opened) {
  return ((_capture) => {
    return $redraw.use_effect_(_capture, [current_opened]);
  })(
    () => {
      return $bool.guard(
        $option.is_none(current_opened),
        () => { return undefined; },
        () => {
          return $unsafe.listen_dom_click(
            () => { return set_current_opened(new None()); },
          );
        },
      );
    },
  );
}

function use_popover_context() {
  let $ = $redraw.use_state(new None());
  let current_opened = $[0];
  let set_current_opened = $[1];
  $scroll.use_freeze($option.is_some(current_opened));
  use_document_click(current_opened, set_current_opened);
  let open = (id) => { return set_current_opened(new Some(id)); };
  let close = () => { return set_current_opened(new None()); };
  return new Popover(current_opened, open, close);
}

function default_value() {
  return new Popover(
    new None(),
    (_) => { return undefined; },
    () => { return undefined; },
  );
}

export function provider() {
  let context = $context.context(context_name, default_value);
  return $redraw.component_(
    "Freyja.Providers(Popover)",
    (children) => {
      let popover = use_popover_context();
      return $redraw.provider(context, popover, children);
    },
  );
}

/**
 * Store an `opened` boolean for a component. Every time `use_opened` is
 * called by a component, a unique instance for that component is created.
 * Every time the component decides to "open" (whatever it means, it can be
 * a dropdown that opens itself, a tooltip, or whatever), the context will
 * changes to that unique ID, indicating that the component is opened.
 *
 * Everytime the popover context opens, it automatically subcribes to click in
 * DOM to automatically close the component. It also freeze the scroll as long
 * as the popover context is opened.
 *
 * ```gleam
 * use _ <- redraw.component_("MyComponent")
 * let #(opened, set_opened) = popover.use_opened()
 * let on_click = fn (handler) { event.on_click(fn (_) { handler() }) })
 * html.div([], [
 *   html.button([on_click(fn () { set_opened(True) })], [html.text("Open")]),
 *   case opened {
 *     True -> html.div([on_click(fn () { set_opened(False) })], [html.text("I'm opened!")])
 *     False -> html.none()
 *   }
 * ])
 * ```
 */
export function use_opened() {
  let id = $redraw.use_id();
  let $ = $context.get(context_name);
  let context;
  if ($ instanceof Ok) {
    context = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "ds/providers/popover",
      49,
      "use_opened",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 1653,
        end: 1703,
        pattern_start: 1664,
        pattern_end: 1675
      }
    )
  }
  let popover = $redraw.use_context(context);
  let opened = isEqual(popover.current_opened, new Some(id));
  return [
    opened,
    (opened) => {
      if (opened) {
        return popover.open(id);
      } else {
        return popover.close();
      }
    },
  ];
}

/**
 * - When popover context closes, close the local state.
 * 
 * @ignore
 */
function use_sync_local_opened(popover_opened, set_opened) {
  return ((_capture) => {
    return $redraw.use_effect(_capture, [popover_opened]);
  })(
    () => {
      if (popover_opened) {
        return undefined;
      } else {
        return set_opened((_) => { return new None(); });
      }
    },
  );
}

/**
 * - When local state opens, open the popover context.
 * - When local state closes, closes the popover context.
 * 
 * @ignore
 */
function use_sync_popover_opened(multi_opened, set_popover_opened) {
  return ((_capture) => { return $redraw.use_effect(_capture, [multi_opened]); })(
    () => {
      let is_opened = $option.is_some(multi_opened);
      return set_popover_opened(is_opened);
    },
  );
}

/**
 * Alternate version of `use_opened`, that accepts the opened child ID instead
 * of a single boolean. For typically use cases, like dropdowns or combobox,
 * you should use `use_opened`, but sometimes more complex use cases arise,
 * like a tooltip, that includes multiple tabs, and only one should be opened
 * at a time. When the tab is opened, the popover context should behaves like
 * if a popover has been opened, and it should not changes if the `opened`
 * value changes.
 *
 * ```gleam
 * use _ <- redraw.component_("MyComponent")
 * let #(opened, set_opened) = popover.use_multi_opened()
 * let on_click = fn (handler) { event.on_click(fn (_) { handler() }) })
 * html.div([], [
 *   html.button([on_click(fn () { set_opened(Some("1"))})], [html.text("Open")]),
 *   case opened {
 *     Some("1") -> html.div([on_click(fn () { set_opened(None) })], [html.text("I'm opened!")])
 *     Some("2") -> html.div([on_click(fn () { set_opened(None) })], [html.text("I'm opened!")])
 *     _ -> html.none()
 *   }
 * ])
 * ```
 */
export function use_multi_opened() {
  let $ = use_opened();
  let popover_opened = $[0];
  let set_popover_opened = $[1];
  let $1 = $redraw.use_state_(new None());
  let opened = $1[0];
  let set_opened = $1[1];
  use_sync_popover_opened(opened, set_popover_opened);
  use_sync_local_opened(popover_opened, set_opened);
  return [opened, set_opened];
}
